import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, Timestamp, doc, getDoc } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { liveGameDocs } from '../../helpers/games';
import GameByStatusCardPublicBasic from '../ui/gameStatusCards/gameByStatusCardPublicBasic';
import { useColorMode } from '../../hooks/colorModeProvider';

const PublicScoringSection = () => {
  const { eventId, sectionId } = useParams();
  const [games, setGames] = useState([]);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const snapshotSeq = useRef(0);
  const dispatch = useDispatch();

  const { mode, setMode } = useColorMode();

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  // Fetch section details
  useEffect(() => {
    if (!sectionId) return;

    const fetchSection = async () => {
      try {
        const sectionDoc = await getDoc(doc(store, 'sections', sectionId));
        if (sectionDoc.exists()) {
          setSection({ id: sectionDoc.id, ...sectionDoc.data() });
        }
      } catch (e) {
        console.error('Error fetching section:', e);
      }
    };

    fetchSection();
  }, [sectionId]);

  // Fetch games for the section
  useEffect(() => {
    if (!eventId || !sectionId) {
      setError('Missing event ID or section ID parameter');
      setLoading(false);
      return;
    }

    const gamesRef = collection(store, 'games');
    const oneHourAgo = Timestamp.fromMillis(Date.now() - 60 * 60 * 1000);

    const q = query(gamesRef, where('event', '==', eventId), where('section', '==', sectionId));

    let active = true;
    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        // Venue lookups can finish out of order after two quick writes, so
        // only the newest snapshot may replace the games on screen.
        const seq = ++snapshotSeq.current;
        const gamesData = await Promise.all(
          liveGameDocs(querySnapshot).map(async (docSnap) => {
            const gameData = { id: docSnap.id, ...docSnap.data() };
            // If venue is an ID, fetch venue details
            if (gameData.venue && typeof gameData.venue === 'string') {
              try {
                const venueDoc = await getDoc(doc(store, 'venues', gameData.venue));
                if (venueDoc.exists()) {
                  gameData.venue = { id: venueDoc.id, ...venueDoc.data() };
                }
              } catch (e) {
                console.log(e);
              }
            }
            return gameData;
          })
        );

        if (!active || seq !== snapshotSeq.current) return;

        console.log(gamesData);

        // Filter out completed games older than 1 hour
        const filteredGames = gamesData.filter((game) => {
          // Show all games that are NOT completed
          if (game.status !== 3) return true;
          // For completed games, only show if updated within the last hour
          return game.updatedAt && game.updatedAt.toMillis() >= oneHourAgo.toMillis();
        });

        // Sort games by round and status
        const sortedGames = filteredGames.sort((a, b) => {
          // Sort by round (ascending)
          if (a.round !== b.round) {
            return a.round - b.round;
          }
          // Then put completed games at the end
          if (a.status === 3 && b.status !== 3) return 1;
          if (a.status !== 3 && b.status === 3) return -1;
          return 0;
        });

        setGames(sortedGames);
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (!active) return;
        console.error('Error fetching games:', err);
        setError('Failed to load games');
        setLoading(false);
      }
    );

    return () => {
      active = false;
      snapshotSeq.current += 1;
      unsubscribe();
    };
  }, [eventId, sectionId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading games...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {section?.name || 'Section'}
      </Typography>

      {games.length === 0 ? (
        <Typography variant="h6">No games found in this section</Typography>
      ) : (
        <Stack alignItems={'center'} gap={2}>
          <Typography variant="h6" gutterBottom>
            Games ({games.length} game{games.length !== 1 ? 's' : ''})
          </Typography>

          {games.map((game) => (
            <GameByStatusCardPublicBasic key={game.id} game={game} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PublicScoringSection;
